import { DownloadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Table, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { fetchWithdrawalImageUrl, useAddWithdrawal, useInvestments, useWithdrawals } from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import type { Withdrawal } from '../../types';
import { apiDate, date, money } from '../../utils/format';
import { trancheOptionLabel, withdrawalShare } from '../../utils/withdrawalShare';
import PendingDocumentPicker from './PendingDocumentPicker';
import TransactionImageLink from './TransactionImageLink';

export default function WithdrawalsTab({ investorId }: { investorId: number }) {
  const { data, isLoading } = useWithdrawals(investorId);
  const { data: investments } = useInvestments(investorId);
  const { canWrite, hasRole } = useAuth();
  // Investors get a single statement PDF from the Ledger tab instead of per-row receipts.
  const showReceipts = !hasRole('Investor');
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const addWithdrawal = useAddWithdrawal(investorId);

  // Watched so the share-of-investment line can update as the amount is typed.
  const selectedInvestmentId = Form.useWatch('investmentId', form) as number | undefined;
  const enteredAmount = Form.useWatch('amount', form) as number | undefined;

  const openInvestments = (investments ?? []).filter((i) => i.remainingAmount > 0);

  // Total capital still invested across every tranche — the denominator throughout.
  const totalRemaining = (investments ?? []).reduce((sum, i) => sum + i.remainingAmount, 0);

  // Only tranches with principal left can be drawn against. Each option shows its own profit rate,
  // so it is clear which tranche's earning power a withdrawal would be giving up.
  const options = openInvestments.map((i) => ({
    value: i.id,
    label: trancheOptionLabel(i.id, date(i.investmentDate), i.remainingAmount, i.profitRate),
  }));

  const selected = openInvestments.find((i) => i.id === selectedInvestmentId);

  const share = selected
    ? withdrawalShare(enteredAmount, selected.id, selected.remainingAmount, totalRemaining)
    : null;

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await addWithdrawal.mutateAsync({
        investmentId: values.investmentId as number,
        amount: values.amount as number,
        withdrawalDate: apiDate(values.withdrawalDate as dayjs.Dayjs)!,
        reason: values.reason as string | undefined,
        transactionImage,
      });
      message.success('Withdrawal recorded.');
      form.resetFields();
      setTransactionImage(null);
      setOpen(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <>
      {canWrite && (
        <Space style={{ marginBottom: 16 }}>
          <Button
            danger
            type="primary"
            icon={<MinusCircleOutlined />}
            onClick={() => setOpen(true)}
            disabled={options.length === 0}
          >
            Withdraw principal
          </Button>
        </Space>
      )}

      <Table<Withdrawal>
        rowKey="id"
        loading={isLoading}
        dataSource={data ?? []}
        scroll={{ x: 'max-content' }}
        columns={[
          { title: '#', dataIndex: 'id', width: 70 },
          { title: 'Date', dataIndex: 'withdrawalDate', render: date },
          { title: 'From tranche', dataIndex: 'investmentId', render: (v: number) => `#${v}` },
          { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
          { title: 'Remaining in tranche', dataIndex: 'remainingAmount', align: 'right', render: money },
          { title: 'Reason', dataIndex: 'reason', render: (v: string | null) => v ?? '—' },
          {
            title: 'Transaction proof',
            render: (_, row) => (
              <TransactionImageLink
                exists={row.hasTransactionImage}
                title={`Transaction proof — withdrawal #${row.id}`}
                fetchUrl={() => fetchWithdrawalImageUrl(investorId, row.id)}
              />
            ),
          },
          ...(showReceipts
            ? [
                {
                  title: 'Receipt',
                  render: (_: unknown, row: Withdrawal) => (
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() =>
                        downloadFile(`/receipts/withdrawal/${row.id}`, `withdrawal_receipt_${row.id}.pdf`).catch((err) =>
                          message.error(errorMessage(err)),
                        )
                      }
                    >
                      PDF
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal
        title="Withdraw principal"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={addWithdrawal.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false} initialValues={{ withdrawalDate: dayjs() }}>
          <Form.Item name="investmentId" label="Investment tranche" rules={[{ required: true }]}>
            <Select options={options} placeholder="Select a tranche" />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount (PKR)"
            rules={[{ required: true }, { type: 'number', min: 0.01, message: 'Amount must be greater than zero.' }]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>
          {share && (
            <Alert
              type={share.overdrawn ? 'error' : 'info'}
              showIcon
              style={{ marginBottom: 16, marginTop: -8 }}
              message={share.text}
            />
          )}

          <Form.Item name="withdrawalDate" label="Withdrawal date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="withdrawal-image"
            label="Transaction proof (optional)"
            hint="Receipt or transfer screenshot proving the investor received the amount · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
